import React, { useState } from 'react';
import { Button } from '@/shared/ui/button'
import { AlertTriangle, ChevronDown, FileAudio, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'
import DraftAudioPreview from '@/features/reports/DraftAudioPreview'

function formatDraftDate(ts) {
  const d = new Date(ts);
  const month = d.toLocaleString(undefined, { month: 'short' });
  const day = d.getDate();
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${month} ${day} · ${hours}:${mins}`;
}

export default function DraftsBanner({ drafts, onRetry, onDiscard }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!drafts || drafts.length === 0) return null;

  const message = t('drafts.unsavedCount', {
    count: drafts.length,
    defaultValue_one: 'You have {{count}} unsaved recording',
    defaultValue_other: 'You have {{count}} unsaved recordings',
    defaultValue: `You have ${drafts.length} unsaved recording${drafts.length > 1 ? 's' : ''}`,
  });

  return (
    <div className="mb-4 mt-3 rounded-xl border border-amber-200 bg-amber-50">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-amber-100/50 rounded-t-xl"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mb-[2px]" />
          <span className="text-sm font-semibold text-amber-800 mt-[2px]">{message}</span>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-amber-800 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div className="px-4 pb-4 pt-0 flex flex-col gap-2 border-t border-amber-100/80">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className="grid grid-cols-2 sm:grid-cols-[auto_1fr_auto] gap-2 sm:gap-3 items-center rounded-lg bg-white border border-amber-100 px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0 col-start-1 row-start-1">
                <FileAudio className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm text-amber-900 whitespace-nowrap truncate">
                  {formatDraftDate(draft.createdAt)}
                </span>
              </div>

              <div className="min-w-0 draft-row__audio col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1 ml-5 sm:ml-0">
                <DraftAudioPreview audioBlob={draft.audioBlob} recordingTime={draft.recordingTime} />
              </div>

              <div className="flex items-center gap-2 justify-self-end col-start-2 row-start-1 sm:col-start-3">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => onRetry(draft)}
                      >
                        <RotateCcw className="h-3.5 w-3.5 text-green-500 hover:text-green-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      <p>{t('drafts.retry', { defaultValue: 'Retry' })}</p>
                    </TooltipContent>
                  </Tooltip>

                  <AlertDialog>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        <p>{t('drafts.discard', { defaultValue: 'Discard' })}</p>
                      </TooltipContent>
                    </Tooltip>
                    <AlertDialogContent className="w-fit">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t('drafts.discardTitle', { defaultValue: 'Discard this recording?' })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('drafts.discardDescription', {
                            defaultValue: 'This recording will be permanently deleted and cannot be recovered.',
                          })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="flex sm:justify-start">
                        <AlertDialogCancel className="w-full">
                          {t('common.no', { defaultValue: 'No' })}
                        </AlertDialogCancel>
                        <AlertDialogAction className="w-full" onClick={() => onDiscard(draft.id)}>
                          {t('common.yes', { defaultValue: 'Yes' })}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TooltipProvider>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}